import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { Play, Pause } from 'lucide-react';

interface AudioPlayerProps {
  src: string;
  isUser?: boolean;
}

const AudioPlayer: React.FC<AudioPlayerProps> = ({ src, isUser = false }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [isDragging, setIsDragging] = useState(false);

  const audioRef = useRef<HTMLAudioElement>(null);
  const progressBarRef = useRef<HTMLDivElement>(null);
  const thumbRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<number | null>(null);
  const currentTimeRef = useRef(0);
  const durationRef = useRef(0);
  const lastStateSyncRef = useRef(0);

  // Reduced bars to 18 to fit better in w-[260px]
  const waveformBars = useMemo(() => {
    return Array.from({ length: 18 }, () => Math.floor(Math.random() * 40) + 15);
  }, []);

  const formatTime = (seconds: number) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s < 10 ? '0' : ''}${s}`;
  };

  const updateThumbPosition = useCallback((time: number) => {
    const durationValue = durationRef.current;
    const percentage = durationValue ? (time / durationValue) * 100 : 0;
    if (thumbRef.current) {
      thumbRef.current.style.left = `${percentage}%`;
    }
  }, []);

  const syncCurrentTime = useCallback((time: number) => {
    currentTimeRef.current = time;
    updateThumbPosition(time);
  }, [updateThumbPosition]);

  const cancelProgressAnimation = useCallback(() => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }
  }, []);

  // --- Animation Loop for Smoothness without re-rendering every frame ---
  const updateProgress = useCallback(() => {
    if (audioRef.current) {
      const curr = audioRef.current.currentTime;
      syncCurrentTime(curr);

      const now = performance.now();
      if (now - lastStateSyncRef.current > 250) {
        lastStateSyncRef.current = now;
        setCurrentTime(curr);
      }

      if (!audioRef.current.paused) {
        animationRef.current = requestAnimationFrame(updateProgress);
      } else {
        animationRef.current = null;
      }
    }
  }, [syncCurrentTime]);

  const startProgressAnimation = useCallback(() => {
    cancelProgressAnimation();
    animationRef.current = requestAnimationFrame(updateProgress);
  }, [cancelProgressAnimation, updateProgress]);

  const resetPlaybackState = useCallback(() => {
    cancelProgressAnimation();
    currentTimeRef.current = 0;
    durationRef.current = 0;
    lastStateSyncRef.current = 0;
    setIsPlaying(false);
    setDuration(0);
    setCurrentTime(0);
    updateThumbPosition(0);

    if (audioRef.current) {
      try {
        audioRef.current.currentTime = 0;
      } catch {
        // Some browsers reject seeking while a new object URL is still loading.
      }
    }
  }, [cancelProgressAnimation, updateThumbPosition]);

  const togglePlay = async () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying || !audio.paused) {
      audio.pause();
      cancelProgressAnimation();
      setCurrentTime(currentTimeRef.current);
      setIsPlaying(false);
      return;
    }

    try {
      if (audio.readyState < 1) {
        audio.load();
      }

      await audio.play();
      setIsPlaying(true);
      startProgressAnimation();
    } catch (error) {
      console.warn('No se pudo reproducir el audio.', error);
      cancelProgressAnimation();
      setIsPlaying(false);
      setCurrentTime(currentTimeRef.current);
    }
  };

  const toggleSpeed = () => {
    if (!audioRef.current) return;
    const newRate = playbackRate === 1 ? 1.5 : playbackRate === 1.5 ? 2 : 1;
    audioRef.current.playbackRate = newRate;
    setPlaybackRate(newRate);
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      const nextDuration = audioRef.current.duration;
      const safeDuration = Number.isFinite(nextDuration) ? nextDuration : 0;
      durationRef.current = safeDuration;
      setDuration(safeDuration);
      updateThumbPosition(currentTimeRef.current);
    }
  };

  const handlePlay = () => {
    setIsPlaying(true);
    if (!animationRef.current) {
      startProgressAnimation();
    }
  };

  const handlePause = () => {
    cancelProgressAnimation();
    setIsPlaying(false);
    setCurrentTime(currentTimeRef.current);
  };

  const handleAudioError = () => {
    cancelProgressAnimation();
    setIsPlaying(false);
    setCurrentTime(currentTimeRef.current);
  };

  const handleEnded = () => {
    setIsPlaying(false);
    syncCurrentTime(0);
    setCurrentTime(0);
    if (audioRef.current) audioRef.current.currentTime = 0;
    cancelProgressAnimation();
  };

  // --- Scrubbing Logic ---

  const calculateTimeFromEvent = useCallback((clientX: number) => {
    if (!progressBarRef.current) return 0;
    const rect = progressBarRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const width = rect.width;
    const percentage = Math.max(0, Math.min(1, x / width));
    return percentage * (duration || 0);
  }, [duration]);

  const seekToTime = useCallback((time: number) => {
    const boundedTime = Math.max(0, Math.min(duration || 0, time));
    syncCurrentTime(boundedTime);
    setCurrentTime(boundedTime);
    if (audioRef.current) audioRef.current.currentTime = boundedTime;
  }, [duration, syncCurrentTime]);

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
    seekToTime(calculateTimeFromEvent(e.clientX));
  };

  const handleProgressKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!duration) return;

    const step = 5;
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      seekToTime(currentTimeRef.current - step);
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      seekToTime(currentTimeRef.current + step);
    } else if (e.key === 'Home') {
      e.preventDefault();
      seekToTime(0);
    } else if (e.key === 'End') {
      e.preventDefault();
      seekToTime(duration);
    }
  };

  useEffect(() => {
    const handlePointerMove = (e: PointerEvent) => {
      if (isDragging) {
        e.preventDefault();
        const newTime = calculateTimeFromEvent(e.clientX);
        syncCurrentTime(newTime);
        setCurrentTime(newTime);
        if (audioRef.current) audioRef.current.currentTime = newTime;
      }
    };

    const handlePointerUp = (e: PointerEvent) => {
      if (isDragging) {
        e.preventDefault();
        setIsDragging(false);
        setCurrentTime(currentTimeRef.current);
        if (isPlaying) {
          animationRef.current = requestAnimationFrame(updateProgress);
        }
      }
    };

    if (isDragging) {
      window.addEventListener('pointermove', handlePointerMove);
      window.addEventListener('pointerup', handlePointerUp);
      cancelProgressAnimation();
    }

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
  }, [calculateTimeFromEvent, cancelProgressAnimation, isDragging, isPlaying, syncCurrentTime, updateProgress]);

  useEffect(() => {
    return () => cancelProgressAnimation();
  }, [cancelProgressAnimation]);

  useEffect(() => {
    resetPlaybackState();
    audioRef.current?.load();
  }, [resetPlaybackState, src]);


  const progressPercentage = duration ? (currentTime / duration) * 100 : 0;

  // Visual Styles
  const iconFill = isUser ? 'fill-white text-white' : 'fill-muted text-muted';
  const barColor = isUser ? 'bg-white opacity-50' : 'bg-muted opacity-50';
  const thumbColor = isUser ? 'bg-white' : 'bg-juris';
  const textColor = isUser ? 'text-white/80' : 'text-muted';
  const speedBtnBg = isUser ? 'bg-action/40 text-white' : 'bg-raised text-ink';

  const timeDisplay = (!isPlaying && currentTime === 0) ? formatTime(duration) : formatTime(currentTime);

  return (
    <div className="flex items-center w-full h-12 gap-2 relative">
      <audio
        ref={audioRef}
        src={src}
        onLoadedMetadata={handleLoadedMetadata}
        onPlay={handlePlay}
        onPause={handlePause}
        onError={handleAudioError}
        onEnded={handleEnded}
      />

      {/* 1. Play/Pause Button */}
      <button
        type="button"
        onClick={togglePlay}
        className="flex-shrink-0 z-10 w-8 h-8 rounded-full flex items-center justify-center focus-visible:ring-2 focus-visible:ring-juris focus-visible:ring-offset-2"
        aria-label={isPlaying ? 'Pausar audio' : 'Reproducir audio'}
      >
        {isPlaying ? (
          <Pause size={20} className={iconFill} />
        ) : (
          <Play size={20} className={iconFill} />
        )}
      </button>

      {/* 2. Middle Section (Waveform + Absolute Timer) */}
      <div className="flex-1 relative h-full flex items-center min-w-0">

        {/* Waveform Container */}
        <div
          ref={progressBarRef}
          className="w-full h-8 flex items-center cursor-pointer touch-none relative focus-visible:ring-2 focus-visible:ring-juris focus-visible:ring-offset-2 rounded"
          onPointerDown={handlePointerDown}
          onKeyDown={handleProgressKeyDown}
          role="slider"
          tabIndex={0}
          aria-label="Posicion del audio"
          aria-valuemin={0}
          aria-valuemax={Math.floor(duration || 0)}
          aria-valuenow={Math.floor(currentTime || 0)}
          aria-valuetext={`${formatTime(currentTime)} de ${formatTime(duration)}`}
        >
          {/* Static Bars */}
          <div className="flex items-center justify-between w-full h-full pointer-events-none">
            {waveformBars.map((height, i) => (
              <div
                key={i}
                className={`w-[4px] rounded-full ${barColor}`}
                style={{ height: `${height}%` }}
              ></div>
            ))}
          </div>

          {/* Animated Thumb */}
          <div
            ref={thumbRef}
            className={`absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full shadow-sm z-10 ${thumbColor}`}
            style={{
              left: `${progressPercentage}%`,
              transform: `translate(-50%, -50%) ${isPlaying || isDragging ? 'scale(1.2)' : 'scale(1)'}`,
              transition: isDragging ? 'none' : 'transform 0.1s ease',
            }}
          ></div>
        </div>

        {/* Absolute Timer (Bottom Left of this section) */}
        <span className={`absolute bottom-0 left-0 text-[9px] font-mono leading-none ${textColor}`}>
          {timeDisplay}
        </span>
      </div>

      {/* 3. Speed Button */}
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); toggleSpeed(); }}
        className={`flex-shrink-0 text-[9px] font-bold w-6 h-6 flex items-center justify-center rounded-full transition-colors ${speedBtnBg}`}
        aria-label={`Cambiar velocidad de reproduccion. Actual ${playbackRate}x`}
      >
        {playbackRate}x
      </button>
    </div>
  );
};

export default AudioPlayer;
